import { useState, useEffect } from 'react';
import moment from 'moment';
import styles from './index.module.less';
import { downLoadFile } from '@/utils/utils';
import { EmptyFileColor } from 'patternx-svg';
import PtxTable from '@/components/ptx-table';
import classNames from 'classnames';
import { Button, Modal } from 'antd';
import { getExportTaskApi, getExportTaskUrl } from '@/services/initial';

type ItemList = {
  createdAt: string;
  createdBy: string;
  enterpriseId: string;
  fileName: string;
  id: string;
  status: string;
  statusText: string;
  storageFileId: string;
  suffix: string;
  userId: string;
  [key: string]: any;
};

const colorMap = {
  waiting: 'warning',
  exporting: 'info',
  success: 'success',
  fail: 'error',
};

const statusText = {
  waiting: '未开始',
  exporting: '导出中',
  success: '导出成功',
  fail: '导出失败',
};

interface RecordExport {
  open: boolean;
  onCancel: () => void;
}

const ExportRecordModal = (props: RecordExport) => {
  const { open, onCancel } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ItemList[]>();
  const [pageInfo, setPageInfo] = useState({ page: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);

  const getData = async (params?: { page: number; pageSize: number }) => {
    setLoading(true);
    setPageInfo({
      ...pageInfo,
      page: params?.page || 1,
      pageSize: params?.pageSize || 10,
    });
    const res = await getExportTaskApi({
      page: params?.page || pageInfo.page,
      pageSize: params?.pageSize || pageInfo.pageSize,
    }).catch(() => {
      setLoading(false);
    });
    if (res?.success) {
      setData(res?.data?.details?.list || []);
      setTotal(res?.data?.details?.page?.total || 0);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getData();
    }
  }, [open]);

  const downHandle = async (row: ItemList) => {
    const res = await getExportTaskUrl(row.id);
    if (res?.success) {
      const datas = res?.data?.details;
      if (datas?.downloadUrl) {
        downLoadFile(datas.downloadUrl, datas?.fileName || '构件.zip');
      }
    }
  };

  const columns = [
    {
      title: '文件名称',
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      // width: 240,
    },
    {
      title: '格式',
      dataIndex: 'suffix',
      key: 'suffix',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (val: string, record: any) => {
        const status: keyof typeof colorMap = record.status;
        const key = `${styles[colorMap[status]]}`;
        return (
          <div className={classNames(styles.statusWrap, { [key]: 1 })}>
            <div className={styles.badge} />
            <div className={styles.text}>{statusText[status]}</div>
          </div>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (val: string) => {
        return moment(val).format('YYYY-MM-DD');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      dataIndex: 'action',
      render: (_val: string, row: any) => {
        if (row?.status !== 'success') {
          return null;
        }
        return (
          <Button
            type="link"
            onClick={() => {
              downHandle(row);
            }}
            style={{ padding: 0, textAlign: 'left' }}
          >
            下载
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title={'我的导出任务'}
      open={open}
      maskClosable={false}
      onCancel={onCancel}
      width={720}
      footer={[
        <Button onClick={onCancel} key="export-close">
          关闭
        </Button>,
      ]}
    >
      <div className={styles.main}>
        <PtxTable
          showType={1}
          loading={loading}
          dataSource={data}
          columns={columns}
          noBorder={true}
          empty={{
            show: data?.length === 0,
            isEmpty: true,
            describe: '没有导出记录',
            icon: <EmptyFileColor />,
          }}
          pagination={{
            total: total || 0,
            current: pageInfo.page,
            pageSize: pageInfo?.pageSize || 10,
            onChange: (page, pageSize) => {
              getData({ page, pageSize: pageSize || 10 });
            },
          }}
        />
      </div>
    </Modal>
  );
};

export default ExportRecordModal;
